import { isApiError } from "@sagot/api-client";
import { ZOHO_SYNC_DIRECTIONS, ZOHO_SYNC_NEEDS_SCOPES, ZOHO_SYNC_OBJECTS, type ZohoSyncDirection, type ZohoSyncObject } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Badge, Button, ErrorState, Field, Icon, Select, Skeleton, Switch, toast } from "@sagot/ui";
import { useId, useState } from "react";
import { useErrorMessage } from "../../../../lib/api-errors";
import { useClient } from "../../../../session/session-provider";
import { useFormatters } from "../../shared";
import { useZohoMutations, useZohoStatus } from "../use-zoho";
import { useZohoSync, useZohoSyncMutations } from "../use-zoho-sync";
import { isZohoAuthorizeUrl } from "../zoho-model";
import { canSwitchOn, runCodeOf, totalsOf, type ZohoSync } from "../zoho-sync-model";
import { useCanManage } from "./panel-kit";
import { ZOHO_I18N } from "./zoho-card-parts";
import { ZohoFieldMappingDialog } from "./zoho-field-mapping-dialog";

const K = `${ZOHO_I18N}.contactsSync`;
const NOT_FOUND = 404;
const OBJECT_ROW: Readonly<Record<ZohoSyncObject, { name: string; hint: string }>> = {
  contact: { name: "contactsRow", hint: "contactsHint" },
  account: { name: "accountsRow", hint: "accountsHint" },
};

function CardSkeleton() {
  return (
    <article className="card card-body md:col-span-2" aria-busy="true" data-testid="zoho-sync-loading">
      <div className="flex items-start gap-3">
        <Skeleton className="size-10 shrink-0" />
        <div className="min-w-0 flex-1 space-y-2">
          <Skeleton className="h-5 w-[220px]" />
          <Skeleton className="h-4 w-full max-w-md" />
        </div>
      </div>
      <Skeleton className="mt-4 h-24 w-full" />
    </article>
  );
}

function SyncTable({ sync }: { sync: ZohoSync }) {
  const { t } = useTranslation();
  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- focusable so keyboard users can scroll it (axe scrollable-region-focusable)
    <div className="mt-4 overflow-x-auto focus-visible:outline-2 focus-visible:outline-primary" role="region" tabIndex={0} aria-label={t(`${K}.tableCaption`)} data-testid="zoho-sync-table">
      <table className="w-full min-w-[19rem] text-left text-sm">
        <thead>
          <tr className="border-b border-line text-xs text-fg-muted">
            <th scope="col" className="py-2 pr-3 font-semibold">{t(`${K}.tableRecords`)}</th>
            <th scope="col" className="py-2 pr-3 font-semibold">{t(`${K}.tableDirection`)}</th>
            <th scope="col" className="py-2 pr-3 font-semibold">{t(`${K}.tableLinked`)}</th>
            <th scope="col" className="hidden py-2 font-semibold sm:table-cell">{t(`${K}.tableStatus`)}</th>
          </tr>
        </thead>
        <tbody>
          {ZOHO_SYNC_OBJECTS.map((object) => {
            const links = sync.links[object];
            return (
              <tr key={object} className="border-b border-line last:border-0" data-testid={`zoho-sync-row-${object}`}>
                <th scope="row" className="py-2.5 pr-3 font-normal">
                  <span className="block font-semibold">{t(`${K}.${OBJECT_ROW[object].name}`)}</span>
                  <span className="block text-xs text-fg-muted">{t(`${K}.${OBJECT_ROW[object].hint}`)}</span>
                </th>
                <td className="py-2.5 pr-3">{t(`${K}.directions.${sync.direction}`)}</td>
                <td className="py-2.5 pr-3">
                  {t(`${K}.linkedCount`, { count: links?.linked ?? 0 })}
                  {links && links.deleted_in_zoho > 0 ? <span className="block text-xs text-fg-muted">{t(`${K}.deletedInZoho`, { count: links.deleted_in_zoho })}</span> : null}
                </td>
                <td className="hidden py-2.5 sm:table-cell">
                  <Badge tone={sync.enabled ? "success" : "neutral"} dot>{t(`${K}.${sync.enabled ? "statusOn" : "statusOff"}`)}</Badge>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function LastRun({ sync }: { sync: ZohoSync }) {
  const { t } = useTranslation();
  const { dateTime } = useFormatters();
  const run = sync.last_run;
  if (!run) return <p className="text-sm text-fg-muted" data-testid="zoho-sync-lastrun">{t(`${K}.noRun`)}</p>;
  const totals = totalsOf(run.counts);
  const code = runCodeOf(run.code);
  return (
    <div className="text-sm" data-testid="zoho-sync-lastrun">
      <p>
        <span className="font-semibold">{t(`${K}.lastRun`)}</span>{" "}
        {t(`${K}.runLine`, { date: dateTime(run.at), here: totals.here, there: totals.there, failed: totals.failed })}
      </p>
      {run.failures.length > 0 ? <p className="mt-1 break-words font-mono text-xs text-fg-muted" data-testid="zoho-sync-failures">{run.failures.join(", ")}</p> : null}
      {code ? <p role="alert" className="mt-1 text-xs font-semibold text-danger" data-testid="zoho-sync-stop">{t(`${K}.runCodes.${code}`)}</p> : null}
    </div>
  );
}

/** The grant was made for leads only: say so and offer the reconnect that asks Zoho for the Contacts and Accounts scopes. */
function NeedsScopes({ allowed }: { allowed: boolean }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const client = useClient();
  const { connect } = useZohoMutations();
  const redirecting = connect.isPending || connect.isSuccess;
  const reconnect = () => {
    connect.mutate(undefined, {
      onSuccess: ({ authorize_url }) => {
        if (!isZohoAuthorizeUrl(authorize_url, { apiBaseUrl: client.baseUrl, pageOrigin: window.location.origin })) {
          connect.reset();
          toast.error(t(`${ZOHO_I18N}.notConfigured`));
          return;
        }
        window.location.assign(authorize_url);
      },
      onError: (error) => { toast.error(message(error)); },
    });
  };
  return (
    <div role="alert" className="mt-4 flex flex-wrap items-start justify-between gap-3 rounded-md bg-warning-soft p-3 text-sm" data-testid="zoho-sync-needs-scopes">
      <div className="flex min-w-0 flex-1 items-start gap-2.5">
        <Icon name="alert-triangle" className="mt-0.5 size-4 shrink-0 text-warning" />
        <div className="min-w-0">
          <p className="font-semibold">{t(`${K}.needsScopesTitle`)}</p>
          <p className="text-fg-muted">{t(`${K}.needsScopesBody`)}</p>
        </div>
      </div>
      {allowed ? (
        <Button size="sm" loading={redirecting} onClick={reconnect} data-testid="zoho-sync-reconnect">
          <Icon name="refresh-cw" />
          {redirecting ? t(`${ZOHO_I18N}.redirecting`) : t(`${K}.needsScopesButton`)}
        </Button>
      ) : null}
    </div>
  );
}

function CardLoaded({ sync }: { sync: ZohoSync }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const manage = useCanManage("integrations.manage");
  const { save, run } = useZohoSyncMutations();
  const [mapping, setMapping] = useState(false);
  const switchId = useId();
  const allowed = manage.allowed && sync.can_manage;
  const ready = canSwitchOn(sync);
  const change = (patch: { enabled?: boolean; direction?: ZohoSyncDirection }) => {
    save.mutate(
      { enabled: patch.enabled ?? sync.enabled, direction: patch.direction ?? sync.direction },
      { onSuccess: () => { toast.success(t(`${K}.saved`)); }, onError: (error) => { toast.error(message(error)); } },
    );
  };
  const syncNow = () => {
    run.mutate(undefined, {
      onSuccess: (done) => { (done.status === "ok" ? toast.success : toast.info)(t(`${K}.ran`)); },
      onError: (error) => { toast.error(message(error)); },
    });
  };
  return (
    <article className="card card-body md:col-span-2" aria-labelledby="zoho-sync-title" data-testid="zoho-sync-card">
      <div className="flex items-start gap-3">
        <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-lg bg-sunken text-fg-muted">
          <Icon name="refresh-cw" className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 id="zoho-sync-title" className="font-bold">{t(`${K}.title`)}</h3>
            <Badge tone="brand" data-testid="zoho-sync-proposed">{t(`${K}.proposed`)}</Badge>
          </div>
          <p className="mt-0.5 text-xs text-fg-muted">{t(`${K}.subtitle`)}</p>
        </div>
      </div>
      <SyncTable sync={sync} />
      {sync.connection === ZOHO_SYNC_NEEDS_SCOPES ? <NeedsScopes allowed={allowed} /> : null}
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <div className="flex items-start justify-between gap-4">
          <label htmlFor={switchId} className="min-w-0 text-sm">
            <span className="font-semibold">{t(`${K}.switchLabel`)}</span>
            <span className="block text-fg-muted">{ready ? t(`${K}.switchHint`) : t(`${K}.${sync.connection === ZOHO_SYNC_NEEDS_SCOPES ? "needsScopesHint" : "needsZoho"}`)}</span>
          </label>
          <Switch id={switchId} checked={sync.enabled} disabled={!allowed || save.isPending || (!ready && !sync.enabled)} onCheckedChange={(on) => { change({ enabled: on }); }} data-testid="zoho-sync-switch" />
        </div>
        <Field label={t(`${K}.directionLabel`)}>
          <Select value={sync.direction} disabled={!allowed || save.isPending} onChange={(event) => { change({ direction: event.target.value as ZohoSyncDirection }); }} data-testid="zoho-sync-direction">
            {ZOHO_SYNC_DIRECTIONS.map((direction) => <option key={direction} value={direction}>{t(`${K}.directions.${direction}`)}</option>)}
          </Select>
        </Field>
      </div>
      <div role="note" className="mt-4 flex items-start gap-2.5 rounded-md bg-info-soft p-3 text-sm" data-testid="zoho-sync-rule">
        <Icon name="lock" className="mt-0.5 size-4 shrink-0 text-info" />
        <p className="min-w-0 text-fg-muted">{t(`${K}.rule`)}</p>
      </div>
      <div className="mt-4"><LastRun sync={sync} /></div>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button variant="secondary" size="sm" onClick={() => { setMapping(true); }} data-testid="zoho-sync-mapping-open">
          <Icon name="sliders-horizontal" />
          {t(`${K}.mapping`)}
        </Button>
        <Button size="sm" loading={run.isPending} disabled={!allowed || !sync.enabled || !ready} onClick={syncNow} data-testid="zoho-sync-run">
          <Icon name="refresh-cw" />
          {run.isPending ? t(`${K}.syncing`) : t(`${K}.syncNow`)}
        </Button>
        {allowed ? null : <p className="text-xs text-fg-muted" data-testid="zoho-sync-readonly">{manage.reason ?? t(`${K}.readOnly`)}</p>}
      </div>
      {mapping ? <ZohoFieldMappingDialog sync={sync} open onOpenChange={setMapping} /> : null}
    </article>
  );
}

/**
 * Zoho contacts and accounts sync (CRM-006, proposed): shown under the Zoho CRM card once Zoho is linked and the platform flag is on
 * for the workspace (a 404 hides it). Sagot stays the support record and Zoho the master; the operator's facts only go out.
 */
export function ZohoContactsSyncCard() {
  const { t } = useTranslation();
  const status = useZohoStatus();
  const linked = status.data?.state === "connected" || status.data?.state === "needs_reconnect";
  const sync = useZohoSync(linked);
  if (!linked) return null;
  if (sync.isPending) return <CardSkeleton />;
  if (sync.isError) {
    if (isApiError(sync.error) && sync.error.status === NOT_FOUND) return null;
    return (
      <article className="card card-body md:col-span-2" data-testid="zoho-sync-error">
        <ErrorState headingLevel={3} title={t(`${K}.loadErrorTitle`)} description={t("settings.loadErrorBody")} onRetry={() => void sync.refetch()} retryLabel={t("common.retry")} />
      </article>
    );
  }
  return <CardLoaded sync={sync.data} />;
}
